import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  GripVertical,
  Trash2
} from 'lucide-react';
import { WidgetConfig, ReportTheme } from '../../types';
import {
  TotalTrafficAreaChartWidget,
  UserAcquisitionLineChartWidget,
  OrganicClicksLineChartWidget,
  OrganicCTRNumberWidget,
  AvgRankingPositionLineChartWidget,
  TopLandingPagesTableWidget,
  TopGeoLocationsPieChartWidget,
  AvgEngagementTimeLineChartWidget,
  NewVsReturningUsersBarChartWidget,
  DevicePerformancePieChartWidget,
  BrowserPerformanceBarChartWidget,
  DevicesBrowsersPieChartWidget,
  ReferralWebsitesTableWidget,
  TopCountriesTableWidget,
  TopReferralsTableWidget,
  DailyVisitApiWidget,
  FUNCTIONAL_WIDGETS
} from './functionalWidgets';

interface WidgetRendererProps {
  widget: WidgetConfig;
  reportTheme: ReportTheme;
  onDragStart: (e: React.DragEvent, widgetId: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, targetWidgetId: string) => void;
  onDeleteWidget: (widgetId: string) => void;
}

const WidgetRenderer: React.FC<WidgetRendererProps> = ({
  widget,
  reportTheme,
  onDragStart,
  onDragOver,
  onDeleteWidget
}) => {
  const getWidthClass = (width: string) => {
    switch (width) {
      case '100%': return 'w-full';
      case '50%': return 'w-[49%]';
      case '33%': return 'w-1/3';
      case '25%': return 'w-[23%]';
      default: return 'w-full';
    }
  };

  const getHeightClass = (presentation: string) => {
    switch (presentation) {
      case 'chart-number':
        return 'h-32';
      case 'chart-table':
        return 'h-96';
      default:
        return 'h-96';
    }
  };


  const renderChart = () => {
    if (!widget.data) {
      return (
        <div className={`h-full flex items-center justify-center rounded-lg ${
          reportTheme.theme === 'light' ? 'bg-gray-50' : 'bg-muted/20'
        }`}>
          <div className={`text-center ${
            reportTheme.theme === 'light' ? 'text-gray-500' : 'text-muted-foreground'
          }`}>
            <p className="text-sm">No data available</p>
            <p className="text-xs">Data will appear when API is connected</p>
          </div>
        </div>
      );
    }
    
    // Safety check: ensure data is valid for rendering
    // For chart-number widgets, we allow single values/objects
    // if (widget.presentation === 'chart-number' && !Array.isArray(widget.data) && widget.data !== null) {
    //   console.warn(`Widget ${widget.id} has invalid data structure:`, widget.data);
    //   return (
    //     <div className={`h-full flex items-center justify-center rounded-lg ${
    //       reportTheme.theme === 'light' ? 'bg-gray-50' : 'bg-muted/20'
    //     }`}>
    //       <div className={`text-center ${
    //         reportTheme.theme === 'light' ? 'text-gray-500' : 'text-muted-foreground'
    //       }`}>
    //         <p className="text-sm">Invalid data format</p>
    //         <p className="text-xs">Data is not an array: {JSON.stringify(widget.data)}</p>
    //       </div>
    //     </div>
    //   );
    // }
    
    
    // Check if this is a functional widget first
    const functionalWidgetName = FUNCTIONAL_WIDGETS[widget.id as keyof typeof FUNCTIONAL_WIDGETS];
    if (functionalWidgetName) {
      // Render functional widget based on widget ID
      switch (widget.id) {
        case 'total-traffic':
          return <TotalTrafficAreaChartWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme} 
          />;
        case 'user-acquisition':
          return <UserAcquisitionLineChartWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme} 
          />;
        case 'organic-clicks':
          return <OrganicClicksLineChartWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme} 
          />;
        case 'organic-ctr':
          return <OrganicCTRNumberWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme} 
          />;
        case 'avg-ranking-position':
          return <AvgRankingPositionLineChartWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme} 
          />;
        case 'top-landing-pages':
          return <TopLandingPagesTableWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme} 
          />;
        case 'top-geo-locations':
          return <TopGeoLocationsPieChartWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme} 
          />;
        case 'bounce-rate':
          return <DailyVisitApiWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme}
            aggregation="average"
            metric="Bounce Rate (Organic)"
          />;
        case 'avg-engagement-time-number':
          return <DailyVisitApiWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme}
            aggregation="average"
            metric="Average Engagement Time"
          />;
        case 'avg-engagement-time':
          return <AvgEngagementTimeLineChartWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme} 
          />;
        case 'new-vs-returning-users':
          return <NewVsReturningUsersBarChartWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme} 
          />;
        case 'device-performance':
          return <DevicePerformancePieChartWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme} 
          />;
        case 'browser-performance':
          return <BrowserPerformanceBarChartWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme} 
          />;
        case 'device-performance-pie':
          return <DevicePerformancePieChartWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme} 
          />;
        case 'devices-browsers-pie':
          return <DevicesBrowsersPieChartWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme} 
          />;
        case 'referral-websites':
          return <ReferralWebsitesTableWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme} 
          />;
        case 'top-countries-table':
          return <TopCountriesTableWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme} 
          />;
        case 'top-referrals-table':
          return <TopReferralsTableWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme} 
          />;
        case 'total-page-views-count':
          return <DailyVisitApiWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme}
            aggregation="sum"
            metric="Total Page Views"
          />;
        case 'total-new-users-count':
          return <DailyVisitApiWidget 
            data={widget.data} 
            data_points={widget.data_points}
            report_start_date={widget.report_start_date}
            report_end_date={widget.report_end_date}
            reportTheme={reportTheme}
            aggregation="sum"
            metric="Total New Users"
          />;
      default:
          break;
      }
    }

    // No fallback needed - all widgets are now functional widgets
    return null;
  };

  const baseClasses = `${getWidthClass(widget.width)} p-4`;
  return (
    <Card 
      key={widget.id} 
      className={`${baseClasses} cursor-move ${
        reportTheme.theme === 'light' 
          ? 'border-2 border-gray-200 bg-white shadow-sm' 
          : 'border border-border'
      }`} 
      draggable 
      onDragStart={(e) => onDragStart(e, widget.id)}
      onDragOver={onDragOver}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <CardTitle className={`text-sm ${
              reportTheme.theme === 'light' ? 'text-gray-900' : 'text-foreground'
            }`}>
              <span className="truncate">{widget.metric}</span>
            </CardTitle>
            <CardDescription className={`text-xs mt-1 ${
              reportTheme.theme === 'light' ? 'text-gray-600' : 'text-muted-foreground'
            }`}>
              {widget.business_value}
            </CardDescription>
          </div>
          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
            <Badge variant="outline" className={`text-xs flex-shrink-0 ${
              reportTheme.theme === 'light' 
                ? 'border-gray-300 text-gray-700' 
                : 'border-border text-muted-foreground'
            }`}>
              {widget.source}
            </Badge>
            <GripVertical className={`h-4 w-4 flex-shrink-0 ${
              reportTheme.theme === 'light' ? 'text-gray-400' : 'text-muted-foreground'
            }`} />
            <Button variant="ghost" size="sm" onClick={() => onDeleteWidget(widget.id)} className="flex-shrink-0">
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className={`${getHeightClass(widget.presentation)} `}>
        {renderChart()}
      </CardContent>
    </Card>
  );
};

export default WidgetRenderer;
